import { ContentLink, useSubCategories } from "@robe/client/xcore-ui";
import { Box, BoxProps, Stack, Typography, useTheme } from "@xcorejs/ui";
import Select, { OptionType, SelectProps } from "design-system/xcore/Select";
import { Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "hooks/useMediaQuery";
import { useLayout } from "xcore";
import { Accessory, Product, ProductOverviewPage } from "xcore/types";
import { Paragraph } from "design-system";
import ProductCard from "design-system/robe/Cards/Product";
import { Localized } from "@robe/core";

export type CaptchaStates = "null" | "wrong" | "correct";

type SelectProductCaptchaProps = {
  children?: ReactNode;
  items: Product[];
  setCaptcha?: Dispatch<SetStateAction<CaptchaStates>>;
  newIconText?: Localized;
  _selectProps?: Omit<SelectProps<OptionType<number>, false>, "options" | "isDisabled" | "onChange" | "menuPortalTarget" | "styles">;
} & BoxProps;

/**
 * Basic captcha that validates if user knows of which product type the product that is shown is.
 * Product type categories must be fetched in page props.
 *
 * ! For select menu to render correctly any elements after captcha should be passed as children to this component.
 * */
const SelectProductCaptcha = ({ children, items, setCaptcha, newIconText, _selectProps, ...props }: SelectProductCaptchaProps) => {
  const [options] = useSubCategories<ProductOverviewPage>();
  const [answerValue, setAnswerValue] = useState<number | null>(null);
  const [isAnswerWrong, setIsAnswerWrong] = useState(false);
  const { general, stringify } = useLayout();
  const theme = useTheme();
  const [isMdScreenWidth] = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);

  const filteredOptions = useMemo(
    () => options?.filter((o) => o.content).map((o) => ({ label: stringify(o.name), value: o.id })),
    [options, stringify]
  );

  // Use only items that have `content` in sub category. Memoize this so the product to be guessed does not change with each wrong answer
  const filteredItems = useMemo(
    () =>
      items.filter((item) =>
        filteredOptions.find((option) => item.subCategories?.["product-type"]?.find((subCategory) => subCategory.id === option.value))
      ),
    [filteredOptions, items]
  );

  // Memoize this so the product to be guessed does not change with each wrong answer
  const randomItem = useMemo(() => filteredItems[Math.floor(Math.random() * filteredItems.length)], [filteredItems]);

  useEffect(() => {
    if (answerValue !== null) {
      if (
        Array.isArray(randomItem?.subCategories?.["product-type"]) &&
        randomItem?.subCategories?.["product-type"].map((p) => p.id).includes(answerValue)
      ) {
        setIsAnswerWrong(false);
        setCaptcha?.("correct");

        return;
      }

      setIsAnswerWrong(true);
      setCaptcha?.("wrong");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerValue]);

  return (
    <Stack {...props} direction={{ _: "column", sm: "row" }} gap="3rem" justifyContent="space-between">
      <Stack id="captcha-wrapper" position="relative" direction="column" gap="2rem" wrapItems>
        <Box>
          <Paragraph maxWidth="41rem">{stringify(general.values.captchaInfo)}</Paragraph>
        </Box>

        {randomItem && (
          <Box display={{ _: "block", md: "none" }}>
            <ContentLink content={randomItem} variant="simple" overflow="hidden" target="_blank">
              <ProductCard product={randomItem as ProductCardProduct} width="100%" maxWidth="30rem" />
            </ContentLink>
          </Box>
        )}

        <Box position="relative" {...(isAnswerWrong && { border: "1px solid red" })} width="100%" maxWidth="41rem">
          <Select
            options={filteredOptions}
            isDisabled={!!answerValue && !isAnswerWrong}
            onChange={(v) => setAnswerValue((v as OptionType<number>).value)}
            // Select menu is rendered wrong in portal so it needs to be rendered in wrapper so it is on same level as children
            menuPortalTarget={document.getElementById("captcha-wrapper")}
            styles={{ menuPortal: (styles) => ({ ...styles, zIndex: 999999, top: isMdScreenWidth ? "16rem" : "49rem", left: "2px" }) }}
            {..._selectProps}
          />
        </Box>

        {/* xcoreJS/UI seems not to support visibility attribute */}
        {/* In some scenarios, rendering error message conditionally led to image resize and bad UI => for that reason visibility hidden is used */}
        <div style={{ visibility: isAnswerWrong ? "visible" : "hidden" }}>
          <Typography variant="h5" alignSelf="center" {...(isAnswerWrong && { color: "#e13030" })} fontWeight="bold">
            {stringify(general.values.captchaError)}
          </Typography>
        </div>

        <Box maxWidth="41rem">{children}</Box>
      </Stack>

      {randomItem && (
        <Box display={{ _: "none", md: "block" }}>
          <ContentLink content={randomItem} variant="simple" overflow="hidden" target="_blank">
            <ProductCard newIconText={newIconText} product={randomItem as ProductCardProduct} width="100%" maxWidth="30rem" />
          </ContentLink>
        </Box>
      )}
    </Stack>
  );
};

export default SelectProductCaptcha;

type ProductCardProduct =
  | Product<"images" | "discontinuedGlob" | "discontinuedReg" | "name" | "newIcon" | "ipCertifiedIcon" | "textTag">
  | Accessory<"images" | "discontinuedGlob" | "discontinuedReg" | "name">;
